/**
 * GUC v2 — Guarantors.
 *
 * The wire array stays `borrowers[]` (the loan model's vocabulary, which is what
 * makes Create loan a straight re-post); only the heading says "guarantors",
 * because on a ground-up scenario that is what these people are.
 *
 * WHAT IS DIFFERENT FROM THE RTL FORM — the experience block. Ground-up
 * classification is completed-project experience and nothing else
 * (`ClassificationConfig.classify` → `gucExperienceThresholds`, A+ 4 / A 2 /
 * B 1, with B also needing FICO 700+), so the two ground-up categories lead and
 * carry the thresholds on the label. The other four still COUNT — the
 * normalizer sums the whole `experience` dict into `verifiedExperienceTotal`,
 * which is the number the classifier reads — so they are collected too, under
 * their own sub-label rather than dropped. Leaving them off would undercount
 * cumulative experience and resolve a lower class than the borrower has.
 */
import { Field, useField } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  SZ,
  Section,
  Grid,
  Field2,
  RffText,
  RffMoney,
  RffNum,
  RffStepper,
  RffPercent,
  RffSelect,
  RffSwitch,
  RepeatItem,
  SubLabel,
  AddButton,
} from "../../sizersShared/sizerUi";
import { selectOptionsCitizenshipStatus } from "../../sizersShared/sizerSelectOptions";

/** Nothing in here is a value — a defaulted FICO prices a deal nobody entered. */
export const newGuarantor = ({ isGuarantor = true } = {}) => ({
  isGuarantor,
  firstName: "",
  lastName: "",
});

/**
 * The two categories GUC classification is ABOUT. `ground_up_completed_and_sold`
 * is the spec's named field; the retained-as-rental sibling is the same track
 * record with a different exit and counts identically.
 */
export const GUC_EXPERIENCE_CATEGORIES = [
  {
    key: "ground_up_completed_and_sold",
    label: "Ground-up completed and sold",
  },
  {
    key: "ground_up_retained_as_rental",
    label: "Ground-up retained as rental",
  },
];

/** Counted in the cumulative total, but not what the ground-up class is about. */
export const OTHER_EXPERIENCE_CATEGORIES = [
  { key: "fix_and_flip_sold", label: "Fix & Flip sold" },
  { key: "fix_and_flip_retained_rental", label: "Fix & Flip retained as rental" },
  {
    key: "income_producing_properties_purchased",
    label: "Income-producing properties owned",
  },
  { key: "gc_for_third_party", label: "Projects as GC for a third party" },
];

const ALL_CATEGORIES = [...GUC_EXPERIENCE_CATEGORIES, ...OTHER_EXPERIENCE_CATEGORIES];

const roleLabel = (isGuarantor) => (isGuarantor ? "Guarantor" : "Applicant");

const RoleSub = ({ name }) => (
  <Field name={name} subscription={{ value: true }}>
    {({ input }) => roleLabel(input.value)}
  </Field>
);

/**
 * FICO, required for a US Citizen only.
 *
 * A foreign national prices without a domestic score — the engine derives FN
 * status from `citizenshipStatus` and takes its own path — so requiring one
 * there would block a scenario that prices fine. It is worth saying on a GUC
 * form that FICO still matters: class B needs 700+ on top of the experience
 * threshold, and 660–679 or FN caps the front end at 65%.
 */
const FicoField = ({ name, citizenshipName }) => {
  const { input: citizenship } = useField(citizenshipName, {
    subscription: { value: true },
  });
  const required = citizenship.value === "US Citizen";
  return (
    <Field2
      label="FICO"
      required={required}
      tip={
        required
          ? "Class B needs 700+ on top of the experience threshold. 660–679 caps the front end (LTV/LTC) at 65%."
          : "Not required for a foreign national — but FN caps the front end (LTV/LTC) at 65%."
      }
    >
      <RffNum name={name} placeholder="Score" required={required} />
    </Field2>
  );
};

/**
 * Ground-up track record, summed across ALL guarantors — the same
 * cumulative-across-guarantors rule the engine applies.
 *
 * Gated on something having been entered: a fresh form has nothing to say about
 * a track record nobody has typed yet, and declaring a blank scenario
 * ineligible is a scarier claim than the form has any basis for.
 */
const GucExperienceNote = () => {
  const { input: borrowers } = useField("borrowers", { subscription: { value: true } });
  const rows = borrowers.value || [];

  const anyEntered = rows.some((b) =>
    ALL_CATEGORIES.some((c) => {
      const v = b?.experience?.[c.key];
      return v != null && v !== "";
    }),
  );
  if (!anyEntered) return null;

  const total = rows.reduce(
    (sum, b) =>
      sum +
      GUC_EXPERIENCE_CATEGORIES.reduce(
        (n, c) => n + (Number(b?.experience?.[c.key]) || 0),
        0,
      ),
    0,
  );
  if (total > 0) return null;

  return (
    <Flx
      g={1}
      sx={{
        mb: 2,
        p: "9px 12px",
        borderRadius: SZ.radiusSm,
        background: SZ.badBg,
        border: `1px solid ${SZ.bad}33`,
      }}
    >
      <Txt sx={{ fontSize: 11.5, color: SZ.ink700 }}>
        <Box component="span" sx={{ fontWeight: 700, color: SZ.ink900 }}>
          No ground-up experience entered.
        </Box>{" "}
        Ground-up classifies on completed projects alone, and the GUC leverage
        grid carries no class C row — so this prices with no credit box at all
        rather than a reduced one. The path is a General Contractor completion
        guaranty at reduced overall leverage: turn it on in Loan details to open
        the exception request.
      </Txt>
    </Flx>
  );
};

const MAX_GUARANTORS = 20;

export default function GucGuarantorsSection({ first }) {
  return (
    <Section
      first={first}
      title="Guarantors"
      subtitle="For LLCs detail all entity members. Enter percent ownership with the majority owner first. Non-guarantor ownership percentages are required for the ownership checks."
    >
      <FieldArray name="borrowers">
        {({ fields }) => (
          <>
            <GucExperienceNote />

            {fields.map((f, index) => (
              <RepeatItem
                key={f}
                title={`Guarantor ${index + 1}`}
                sub={<RoleSub name={`${f}.isGuarantor`} />}
                onRemove={fields.length > 1 ? () => fields.remove(index) : undefined}
                removeLabel={`Remove guarantor ${index + 1}`}
                defaultCollapsed={index > 0}
              >
                <SubLabel first>Ownership &amp; role</SubLabel>
                <Grid cols={12}>
                  <Field2 label="Entity ownership %" required span={6}>
                    <RffPercent
                      name={`${f}.entityPercentOwnership`}
                      factor={100}
                      required
                      placeholder="0"
                    />
                  </Field2>
                  <Field2 label="Is a guarantor" span={6}>
                    <RffSwitch name={`${f}.isGuarantor`} />
                  </Field2>
                </Grid>

                <SubLabel>Liquidity &amp; credit</SubLabel>
                <Grid cols={4}>
                  <Field2 label="Citizenship status">
                    <RffSelect
                      name={`${f}.citizenshipStatus`}
                      options={selectOptionsCitizenshipStatus}
                      placeholder="Select"
                    />
                  </Field2>
                  <FicoField
                    name={`${f}.ficoMid`}
                    citizenshipName={`${f}.citizenshipStatus`}
                  />
                  <Field2 label="Cash liquidity">
                    <RffMoney name={`${f}.cashLiquidity`} placeholder="0" />
                  </Field2>
                  <Field2
                    label="Other liquidity"
                    tip="Money market, securities, life insurance cash value, IRA / 401k."
                  >
                    <RffMoney name={`${f}.otherLiquidity`} placeholder="0" />
                  </Field2>
                </Grid>

                <SubLabel note="what the ground-up class is scored on">
                  Ground-up experience
                </SubLabel>
                <Grid cols={2}>
                  {GUC_EXPERIENCE_CATEGORIES.map((c) => (
                    <Field2
                      key={c.key}
                      label={c.label}
                      tip="Class thresholds: A+ 4 · A 2 · B 1 completed projects. Counted cumulatively across every guarantor."
                    >
                      <RffStepper
                        name={`${f}.experience.${c.key}`}
                        placeholder="0"
                        min={0}
                      />
                    </Field2>
                  ))}
                </Grid>

                <SubLabel note="counted in the cumulative total">
                  Other completed projects
                </SubLabel>
                <Grid cols={2}>
                  {OTHER_EXPERIENCE_CATEGORIES.map((c) => (
                    <Field2 key={c.key} label={c.label}>
                      <RffStepper
                        name={`${f}.experience.${c.key}`}
                        placeholder="0"
                        min={0}
                      />
                    </Field2>
                  ))}
                </Grid>

                <SubLabel note="optional until loan creation">Identity</SubLabel>
                <Grid cols={2}>
                  <Field2 label="First name">
                    <RffText name={`${f}.firstName`} placeholder="First name" />
                  </Field2>
                  <Field2 label="Last name">
                    <RffText name={`${f}.lastName`} placeholder="Last name" />
                  </Field2>
                </Grid>
              </RepeatItem>
            ))}

            {fields.length < MAX_GUARANTORS && (
              <AddButton onClick={() => fields.push(newGuarantor())}>
                Add guarantor
              </AddButton>
            )}
          </>
        )}
      </FieldArray>
    </Section>
  );
}
